import { Loading, notify } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useState } from 'react';
import { TdInput } from './TdInput';
import { TypeBasedCell } from './TypeBasedCell';

const getTdBgStyles = ({
  isModified,
  isSelected,
  matchesSearch,
  isDisabled,
}: {
  isModified: boolean;
  isSelected: boolean;
  matchesSearch: boolean;
  isDisabled: boolean;
}) => {
  if (matchesSearch) {
    return 'bg-ds-primary-50';
  }

  if (isSelected) {
    return 'bg-ds-primary-50';
  }

  if (isModified) {
    return 'bg-ds-marker-1-a';
  }

  if (isDisabled) {
    return 'bg-ds-natural-50';
  }

  return '';
};

type TdProps = {
  value: any;
  onNavigate: (tableId: string) => void;
  onSubmit: (value: string | boolean) => Promise<void>;
  onRevert: () => void;
  isColumnSelected: boolean;
  isRowSelected: boolean;
  isModified: boolean;
  isDisabled: boolean;
  isLastRow: boolean;
  isLastColumn: boolean;
  matchesSearch?: boolean;
};

export const Td = ({
  value,
  onNavigate,
  onSubmit,
  onRevert,
  isModified,
  isDisabled,
  isColumnSelected,
  isRowSelected,
  isLastRow,
  isLastColumn,
  matchesSearch,
}: TdProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isTextSelected, setIsTextSelected] = useState(false);
  const isSelected = isColumnSelected || isRowSelected;

  const handleCellClick = (
    type: 'boolean' | 'array' | 'null' | 'object' | 'string'
  ) => {
    if (type === 'string' || type === 'null') {
      setIsTextSelected(true);
    }
    if (type === 'array') {
      console.debug('array cell clicked');
    }
    if (type === 'object') {
      onNavigate(value.object_id);
    }
  };

  return (
    <td
      className={classNames(
        'border-t border-l border-solid border-ds-natural-150 p-0 relative group min-w-[60px] max-w-[400px]',
        getTdBgStyles({
          isModified,
          isSelected,
          matchesSearch,
          isDisabled,
        })
      )}
    >
      <div className="border-x border-solid border-transparent">
        {isSelected && (
          <div
            className={classNames(
              'absolute inset-0 border-ds-primary-300 border-solid pointer-events-none',
              {
                '-top-px bottom-0 border-x': isColumnSelected,
              },
              { 'border-b': isColumnSelected && isLastRow },
              {
                '-left-px right-0 border-y': isRowSelected,
              },
              { 'border-r': isLastColumn }
            )}
          />
        )}
        <TypeBasedCell
          value={value}
          onClick={handleCellClick}
          onRevert={() => onRevert()}
          isModified={isModified}
          isDisabled={isDisabled || isLoading}
        />
        {isTextSelected && (
          <TdInput
            initialValue={value}
            isDisabled={isLoading}
            onSubmit={async (val) => {
              setIsTextSelected(false);
              setIsLoading(true);
              try {
                await onSubmit(val);
              } catch (e) {
                console.error('Failed to update cell', e);
                notify.error('Failed to update cell');
              }
              setIsLoading(false);
            }}
            onCancel={() => setIsTextSelected(false)}
          />
        )}
        {isLoading && (
          <div className="absolute right-0 top-0 bottom-0 flex items-center justify-center px-1.5 backdrop-blur-3xl">
            <Loading color="text-ds-primary" />
          </div>
        )}
      </div>
    </td>
  );
};
